/**
 * The plan header's NOS Deal Stage and Chance of Close (spec Epic 36 / RP-36.1).
 *
 * NOS CRM is the source of truth for the Deal: its stage and percent-to-close show here as
 * NOS has them, refreshed hourly. The plan's own **Chance of Close** sits beside them —
 * it follows NOS until someone picks a value, and that pick needs a comment saying who
 * gave the figure, so the history reads as evidence rather than as numbers. Every change
 * is listed in the popover and in Plan History.
 *
 * When the plan and its Deal disagree, a warning says so — and offers the plan's own
 * Mark Won / Mark Lost step where the plan can take it. Nothing here moves the plan's
 * status by itself.
 *
 * Both sit as chips in the plan's status row (`PlanDealStageChips`, rendered by
 * PlanHeaderFacts); the drift warning stays a full-width alert (`PlanDealDriftWarning`).
 * They share one query, so the two read the same figures.
 */

import { AlertTriangle, ChevronDown, History, Link2 } from "lucide-react";
import { useState } from "react";
import { STATUS_TRIGGER_CLASS, StatusChangeDialog } from "@/components/plans/PlanStatusControl";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { usePlanDealOptions } from "@/hooks/use-plan-deal-options";
import { usePlanDealStage, useSetChanceOfClose, useSetPlanDeal } from "@/hooks/use-plan-deal-stage";
import { CHANCE_OF_CLOSE_COMMENT_MIN } from "@/lib/plan-deal-stage";
import { planStatusLabel } from "@/lib/plan-status-labels";
import { isResourcePlanStatus, type ResourcePlanStatus } from "@/lib/resource-plan-lifecycle";
import { cn } from "@/lib/utils";
import type { PlanDealStageView } from "@/schemas/plan-deal-stage";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

/** The Select's sentinel for "Use NOS" — a percent is never this string. */
const USE_NOS = "nos";

const dateFormat = new Intl.DateTimeFormat("en-US", { day: "numeric", month: "short" });
const dateTimeFormat = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatDay(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (y === undefined || m === undefined || d === undefined) return isoDate;
  return dateFormat.format(new Date(y, m - 1, d));
}

/** "just now" / "12 min ago" / "3 h ago" / "2 days ago". */
function ago(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

function percentText(value: number | null): string {
  return value === null ? "—" : `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

/**
 * NOS Deal Stage and Chance of Close as two chips in the plan's status row, beside the
 * Sales and Resourcing statuses — the same shape, so the four read as one strip of "where
 * this plan stands". With no NOS Deal linked the stage chip is a small dashed "link one"
 * indicator rather than a sentence.
 */
export function PlanDealStageChips({ plan }: { plan: ResourcePlanDetailView }) {
  const { data, isLoading, isError, refetch } = usePlanDealStage(plan.id);
  const [changingDeal, setChangingDeal] = useState(false);

  if (isLoading) {
    return <span className={cn(STATUS_TRIGGER_CLASS, "w-40 animate-pulse bg-muted")} aria-busy />;
  }
  if (isError || data === undefined) {
    return (
      <button
        type="button"
        onClick={() => refetch()}
        className={cn(STATUS_TRIGGER_CLASS, "text-destructive")}
        title="Couldn’t load the NOS Deal Stage. Click to retry."
      >
        <AlertTriangle className="h-3.5 w-3.5" />
        NOS Deal unavailable
      </button>
    );
  }

  return (
    <>
      <DealStageChip view={data} onChangeDeal={() => setChangingDeal(true)} />
      <ChanceOfCloseControl planId={plan.id} view={data} />
      {changingDeal && (
        <ChangeDealDialog planId={plan.id} view={data} onClose={() => setChangingDeal(false)} />
      )}
    </>
  );
}

/** The plan/Deal disagreement warning, which stays a full-width alert above the figures. */
export function PlanDealDriftWarning({ plan }: { plan: ResourcePlanDetailView }) {
  const { data } = usePlanDealStage(plan.id);
  if (data === undefined) return null;
  return <DealDriftWarning plan={plan} view={data} />;
}

function DealStageChip({
  view,
  onChangeDeal,
}: {
  view: PlanDealStageView;
  onChangeDeal: () => void;
}) {
  const deal = view.deal;
  const clickable = view.canChangeDeal;

  if (deal === null) {
    if (!clickable) {
      return (
        <span
          className={cn(
            STATUS_TRIGGER_CLASS,
            "cursor-default border-dashed text-muted-foreground shadow-none hover:bg-transparent",
          )}
          title="No NOS Deal is linked to this plan"
        >
          <Link2 className="h-3.5 w-3.5" />
          No NOS Deal
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={onChangeDeal}
        className={cn(STATUS_TRIGGER_CLASS, "border-dashed text-muted-foreground shadow-none")}
        aria-label="No NOS Deal linked. Link a NOS Deal"
        title="No NOS Deal linked — click to link one"
      >
        <Link2 className="h-3.5 w-3.5" />
        Link NOS Deal
      </button>
    );
  }

  const content = !deal.synced ? (
    <>
      <span className="text-muted-foreground">NOS Deal:</span>
      <span className="text-muted-foreground">Not refreshed yet</span>
    </>
  ) : (
    <>
      <span className="text-muted-foreground">NOS Deal:</span>
      <span className="max-w-64 truncate">
        <span className="font-medium">{deal.stageName ?? "No stage"}</span>
        {deal.expectedClose !== null && (
          <span className="text-muted-foreground"> · close {formatDay(deal.expectedClose)}</span>
        )}
        {!deal.openInNos && <span className="text-muted-foreground"> · closed</span>}
      </span>
    </>
  );
  const title = !deal.synced
    ? `${deal.dealName} — shows after the next sync from NOS, which runs every hour.`
    : `${deal.dealName} — as NOS CRM has it${
        deal.refreshedAt === null ? "" : `, refreshed ${ago(deal.refreshedAt)}`
      }`;

  if (!clickable) {
    return (
      <span
        className={cn(STATUS_TRIGGER_CLASS, "cursor-default hover:bg-transparent")}
        title={title}
      >
        {content}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={onChangeDeal}
      className={STATUS_TRIGGER_CLASS}
      title={`${title}. Click to change the NOS Deal.`}
    >
      {content}
      <ChevronDown className="size-4 opacity-50" />
    </button>
  );
}

function ChanceOfCloseControl({ planId, view }: { planId: string; view: PlanDealStageView }) {
  const [open, setOpen] = useState(false);
  const { percent, source } = view.chanceOfClose;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={STATUS_TRIGGER_CLASS}
          title={
            source === "Staff"
              ? "Set in Staff — click to see who gave it, or to change it"
              : "Following NOS — click to see the history, or to set Staff’s own figure"
          }
        >
          <span className="text-muted-foreground">Chance of Close:</span>
          {percent === null && source === null ? (
            <span className="text-muted-foreground">Not set</span>
          ) : (
            <span>
              <span className="font-medium">{percentText(percent)}</span>
              {source !== null && (
                <span className="text-muted-foreground">
                  {" "}
                  · {source === "Staff" ? "Staff" : "NOS"}
                </span>
              )}
            </span>
          )}
          <ChevronDown className="size-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96">
        {open && <ChanceOfClosePanel planId={planId} view={view} onDone={() => setOpen(false)} />}
      </PopoverContent>
    </Popover>
  );
}

function ChanceOfClosePanel({
  planId,
  view,
  onDone,
}: {
  planId: string;
  view: PlanDealStageView;
  onDone: () => void;
}) {
  const set = useSetChanceOfClose(planId);
  const nosPercent = view.deal?.nosPercent ?? null;
  const followsNos = view.chanceOfClose.source !== "Staff";
  const [choice, setChoice] = useState<string>("");
  const [comment, setComment] = useState("");

  const canUseNos = view.deal !== null && !followsNos;
  const commentOk = comment.trim().length >= CHANCE_OF_CLOSE_COMMENT_MIN;
  const ready = choice !== "" && commentOk && !set.isPending;

  const submit = () => {
    const percent = choice === USE_NOS ? null : Number(choice);
    set.mutate({ percent, comment: comment.trim() }, { onSuccess: onDone });
  };

  return (
    <div className="flex flex-col gap-3">
      <div>
        <p className="text-sm font-medium">Chance of Close</p>
        <p className="text-xs text-muted-foreground">
          {view.deal === null
            ? "This plan has no NOS Deal, so Staff sets it."
            : `NOS says ${percentText(nosPercent)}${
                view.deal.stageName === null ? "" : ` (${view.deal.stageName})`
              }. ${followsNos ? "The plan follows it." : "The plan uses Staff’s own figure."}`}
        </p>
      </div>

      {view.canSetChanceOfClose ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="chance-of-close-value">New Chance of Close</Label>
          <Select value={choice} onValueChange={setChoice}>
            <SelectTrigger id="chance-of-close-value" className="w-full">
              <SelectValue placeholder="Pick a value" />
            </SelectTrigger>
            <SelectContent>
              {view.options.map((o) => (
                <SelectItem key={o} value={String(o)}>
                  {o}%
                </SelectItem>
              ))}
              {canUseNos && (
                <SelectItem value={USE_NOS}>Use NOS ({percentText(nosPercent)})</SelectItem>
              )}
            </SelectContent>
          </Select>
          <Label htmlFor="chance-of-close-comment">Who said so?</Label>
          <Textarea
            id="chance-of-close-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g. Sarah (client sponsor), 16 Oct call: budget approved"
            rows={3}
            maxLength={500}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {commentOk || comment.length === 0
                ? "Kept in the plan’s history."
                : `At least ${CHANCE_OF_CLOSE_COMMENT_MIN} characters.`}
            </span>
            <Button size="sm" disabled={!ready} onClick={submit}>
              {set.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          The Resource Manager or this Account’s Account Manager can change it.
        </p>
      )}

      <ChanceOfCloseHistory view={view} />
    </div>
  );
}

function ChanceOfCloseHistory({ view }: { view: PlanDealStageView }) {
  if (view.history.length === 0) {
    return (
      <p className="border-t border-border pt-2 text-xs text-muted-foreground">
        No changes yet{view.deal === null ? "." : " — it has always followed NOS."}
      </p>
    );
  }
  return (
    <div className="border-t border-border pt-2">
      <p className="mb-1.5 flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <History className="h-3 w-3" /> History
      </p>
      <ol className="flex max-h-56 flex-col gap-2 overflow-y-auto">
        {view.history.map((h) => (
          <li key={h.id} className="text-xs">
            <p>
              <span className="font-medium">
                {h.percent === null ? "Back to NOS" : percentText(h.percent)}
              </span>
              {h.nosPercentAtChange !== null && (
                <span className="text-muted-foreground">
                  {" "}
                  (NOS said {percentText(h.nosPercentAtChange)})
                </span>
              )}
            </p>
            <p className="text-foreground">“{h.comment}”</p>
            <p className="text-muted-foreground">
              {h.setByName ?? "Someone"} · {dateTimeFormat.format(new Date(h.createdAt))}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Where the plan and its NOS Deal disagree — say so, and offer the plan's own next step. */
function DealDriftWarning({
  plan,
  view,
}: {
  plan: ResourcePlanDetailView;
  view: PlanDealStageView;
}) {
  const [target, setTarget] = useState<ResourcePlanStatus | null>(null);
  if (view.drift === null || view.deal === null) return null;

  const allowed = plan.allowedTransitions.filter(isResourcePlanStatus);
  const canManage = plan.canManageStatus ?? plan.canEdit;
  const actions: ResourcePlanStatus[] =
    view.drift === "deal_left_pipeline" && canManage
      ? allowed.filter((s) => s === "Won" || s === "Lost")
      : [];

  return (
    <Alert>
      <AlertTriangle />
      <AlertTitle>
        {view.drift === "won_but_deal_open"
          ? "This plan is Won, but its NOS Deal is still open"
          : "Its NOS Deal has closed"}
      </AlertTitle>
      <AlertDescription>
        <p>
          {view.drift === "won_but_deal_open"
            ? `NOS still shows “${view.deal.dealName}” at ${view.deal.stageName ?? "an open stage"} (${percentText(view.deal.nosPercent)}). Check the NOS Deal — NOS is the source of truth.`
            : `“${view.deal.dealName}” has left the NOS pipeline, but this plan is still ${planStatusLabel(plan.status)}. ${
                actions.length > 0
                  ? "Mark it Won or Lost to match."
                  : "Check the NOS Deal — NOS is the source of truth — and update the plan’s status to match."
              }`}
        </p>
        {actions.length > 0 && (
          <div className="mt-2 flex gap-2">
            {actions.map((to) => (
              <Button
                key={to}
                size="sm"
                variant={to === "Won" ? "default" : "outline"}
                onClick={() => setTarget(to)}
              >
                Mark {planStatusLabel(to)}
              </Button>
            ))}
          </div>
        )}
      </AlertDescription>
      {target !== null && (
        <StatusChangeDialog
          plan={plan}
          to={target}
          isOverride={false}
          onClose={() => setTarget(null)}
        />
      )}
    </Alert>
  );
}

function ChangeDealDialog({
  planId,
  view,
  onClose,
}: {
  planId: string;
  view: PlanDealStageView;
  onClose: () => void;
}) {
  const deals = usePlanDealOptions();
  const setDeal = useSetPlanDeal(planId);
  const [dealId, setDealId] = useState(view.deal?.dealId ?? "");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {view.deal === null ? "Link a NOS Deal" : "Change the NOS Deal"}
          </DialogTitle>
          <DialogDescription>
            The plan shows this NOS Deal’s stage from NOS CRM. Only open NOS Deals for this plan’s
            CRM solution (or with none yet) can be linked.
          </DialogDescription>
        </DialogHeader>
        <SearchableSelect
          options={deals.data?.options ?? []}
          value={dealId}
          onValueChange={setDealId}
          loading={deals.isLoading}
          placeholder="Pick the NOS Deal"
          searchPlaceholder="Search by deal or Account…"
          emptyMessage="No open NOS Deal matches that."
        />
        {deals.isError && (
          <p className="text-sm text-destructive">Couldn’t load NOS Deals right now.</p>
        )}
        <DialogFooter className="sm:justify-between">
          {view.deal !== null ? (
            <Button
              variant="ghost"
              disabled={setDeal.isPending}
              onClick={() => setDeal.mutate(null, { onSuccess: onClose })}
            >
              Remove NOS Deal
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={dealId === "" || dealId === view.deal?.dealId || setDeal.isPending}
              onClick={() => setDeal.mutate(dealId, { onSuccess: onClose })}
            >
              {setDeal.isPending ? "Saving…" : "Link NOS Deal"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
